/**
 * useDepartmentActions — Iter 156.3 (Feb 27, 2026, Yash).
 *
 * Extracted from ChatsPage.jsx. Handlers for setting a lead's department,
 * re-running the keyword router, and the per-chat AI toggles (Reply-Once /
 * Deactivate).
 *
 * The `handleSetDepartment` body has the iter 152.22d fix for null
 * assigned_to (CS handoff with no CS leader unassigns the sales agent —
 * see inline comment).
 */
import { leadActionsApi } from "../lib/api";
import { toast } from "sonner";

export const useDepartmentActions = ({
  selectedLead, setSelectedLead, setChats, loadMessages,
}) => {
  const handleSetDepartment = async (dept) => {
    if (!selectedLead) return;
    try {
      const res = await leadActionsApi.setDepartment(selectedLead.id, dept);
      const leaderName = res?.data?.leader_name;
      // Iter 152.22d (May 2026, Yash) — the backend response may set
      // `assigned_to` to null (when transferring to CS with no CS leader
      // configured, the sales agent must be removed). Previously we used
      // `res?.data?.assigned_to ?? prev.assigned_to` which treated null
      // as "no value" and kept the old sales-agent assignment → user saw
      // "dept=Customer Support, agent=Sumit (sales)". Now we always take
      // the server value when the response came back, including null.
      const serverAssigned = res?.data && "assigned_to" in res.data
        ? res.data.assigned_to
        : undefined;
      toast.success(
        leaderName
          ? `Transferred to ${dept} → ${leaderName}. AI paused.`
          : dept === "Customer Support"
            ? `Transferred to ${dept}. Agent unassigned — CS team will pick it up.`
            : `Department set to ${dept}.`
      );
      setSelectedLead(prev => prev ? {
        ...prev,
        department: dept,
        assigned_to: serverAssigned !== undefined ? serverAssigned : prev.assigned_to,
        assigned_to_name: serverAssigned !== undefined ? (leaderName || null) : prev.assigned_to_name,
        ai_active: false,
      } : prev);
      setChats(prev => prev.map(c => c.id === selectedLead.id
        ? {
            ...c,
            department: dept,
            assigned_to: serverAssigned !== undefined ? serverAssigned : c.assigned_to,
            assigned_to_name: serverAssigned !== undefined ? (leaderName || null) : c.assigned_to_name,
          }
        : c
      ));
      // Pull fresh messages so the system note ("🔄 Transferred …") shows up.
      loadMessages(selectedLead.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to set department");
    }
  };

  // Re-run the support-intent classifier on the last 5 incoming messages.
  // Admin-only — useful when the keyword list was expanded and historic
  // leads need to catch up.
  const handleReroute = async () => {
    if (!selectedLead) return;
    try {
      const { data } = await leadActionsApi.reroute(selectedLead.id);
      if (data.rerouted) {
        toast.success(`Re-routed to ${data.department} · tags: ${(data.issue_tags || []).join(", ") || "—"}`);
        setSelectedLead(prev => prev ? { ...prev, department: data.department, issue_tags: data.issue_tags } : prev);
        setChats(prev => prev.map(c => c.id === selectedLead.id ? { ...c, department: data.department } : c));
      } else {
        toast("No CS keyword matched in the last 5 incoming messages — lead stays in its current department.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-route failed");
    }
  };

  // Fire an AI reply for JUST THIS chat — bypasses the global toggle.
  // Also turns on the per-chat `ai_active` flag so subsequent customer
  // messages on this chat keep getting AI replies until the agent toggles
  // it back off.
  const handleAiReplyOnce = async () => {
    if (!selectedLead) return;
    try {
      const { data } = await leadActionsApi.aiReplyOnce(selectedLead.id);
      setSelectedLead((prev) => prev ? { ...prev, ai_active: true } : prev);
      // Honest feedback: sometimes "Start AI" doesn't fire a reply immediately
      // (when the last message is the agent's outgoing one). Tell the user.
      if (data?.fired === false) {
        toast.success("AI is now active. It will reply to the next customer message.");
      } else {
        toast.success("AI is now active — replying to the customer.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI could not run");
    }
  };

  // Turn OFF the per-chat AI flag (customer messages will fall back to the
  // global office-hours gate).
  const handleAiDeactivate = async () => {
    if (!selectedLead) return;
    try {
      await leadActionsApi.aiToggle(selectedLead.id, false);
      setSelectedLead((prev) => prev ? { ...prev, ai_active: false } : prev);
      toast.success("AI deactivated on this chat.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not deactivate AI");
    }
  };

  return { handleSetDepartment, handleReroute, handleAiReplyOnce, handleAiDeactivate };
};
